import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useFinanceRepository } from '../container';
import type {
  AdjustmentInput,
  CreditInput,
  PaymentEdit,
  PaymentInput,
  PaymentSummary,
} from '../ports/FinanceRepository';
import { keys } from '../query-keys';

/**
 * The money of a case. The request of 8 October 2026 (fourth).
 *
 * Every one of these changes what a case owes or has paid, which the lists,
 * the receipt, the doctor's statement and the overview all read, and writes a
 * row in the case's history. The summary and the statement sit under the
 * orders prefix, so one invalidation refreshes them with the lists.
 */

function refresh(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: keys.orders.all });
  void queryClient.invalidateQueries({ queryKey: ['archive'] });
  void queryClient.invalidateQueries({ queryKey: ['activity'] });
  void queryClient.invalidateQueries({ queryKey: keys.board() });
  // A doctor's record counts its cases, and a payment may have used its credit.
  void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
}

/** Everything about the money of one case. */
export function usePaymentSummary(jobOrderNo: string | null | undefined) {
  const repo = useFinanceRepository();
  return useQuery({
    queryKey: keys.orders.paymentSummary(jobOrderNo ?? ''),
    queryFn: () => repo.summary(jobOrderNo as string),
    enabled: jobOrderNo != null && jobOrderNo !== '',
  });
}

/** A change to one case's money that answers with its summary as it now stands. */
function useSummaryMutation<Input>(run: (input: Input) => Promise<PaymentSummary>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (summary) => {
      queryClient.setQueryData(keys.orders.paymentSummary(summary.jobOrderNo), summary);
    },
    onSettled: () => refresh(queryClient),
  });
}

export function useAddAdjustment() {
  const repo = useFinanceRepository();
  return useSummaryMutation((command: { jobOrderNo: string; input: AdjustmentInput }) =>
    repo.addAdjustment(command.jobOrderNo, command.input),
  );
}

export function useRemoveAdjustment() {
  const repo = useFinanceRepository();
  return useSummaryMutation((command: { jobOrderNo: string; id: string }) =>
    repo.removeAdjustment(command.jobOrderNo, command.id),
  );
}

export function useAddPayment() {
  const repo = useFinanceRepository();
  return useSummaryMutation((command: { jobOrderNo: string; input: PaymentInput }) =>
    repo.addPayment(command.jobOrderNo, command.input),
  );
}

export function useEditPayment() {
  const repo = useFinanceRepository();
  return useSummaryMutation((command: { jobOrderNo: string; id: string; edit: PaymentEdit }) =>
    repo.editPayment(command.jobOrderNo, command.id, command.edit),
  );
}

export function useRemovePayment() {
  const repo = useFinanceRepository();
  return useSummaryMutation((command: { jobOrderNo: string; id: string }) =>
    repo.removePayment(command.jobOrderNo, command.id),
  );
}

/** One doctor's cases as bills, and the doctor's credit with every movement of it. */
export function useDoctorStatement(doctorId: string | null | undefined) {
  const repo = useFinanceRepository();
  return useQuery({
    queryKey: keys.orders.statement(doctorId ?? ''),
    queryFn: () => repo.statement(doctorId as string),
    enabled: doctorId != null && doctorId !== '',
  });
}

export function useAddCredit() {
  const repo = useFinanceRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: { doctorId: string; input: CreditInput }) =>
      repo.addCredit(command.doctorId, command.input),
    onSettled: () => refresh(queryClient),
  });
}

export function useRemoveCredit() {
  const repo = useFinanceRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: { doctorId: string; input: { amount: number; note: string } }) =>
      repo.removeCredit(command.doctorId, command.input),
    onSettled: () => refresh(queryClient),
  });
}

/**
 * A doctor's own prices beside the list's. The lab's request of 17 October 2026,
 * item 11. Asked only of somebody who holds pricing.
 */
export function useDoctorPrices(doctorId: string | null | undefined, enabled = true) {
  const repo = useFinanceRepository();
  return useQuery({
    queryKey: keys.orders.doctorPrices(doctorId ?? ''),
    queryFn: () => repo.doctorPrices(doctorId as string),
    enabled: enabled && doctorId != null && doctorId !== '',
  });
}

function usePriceMutation<Input>(run: (input: Input) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'finance', 'doctor-prices'] });
      // The price list's own screen reads the catalogue, and the log says who changed what.
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useSetDoctorPrice() {
  const repo = useFinanceRepository();
  return usePriceMutation((command: { doctorId: string; itemId: string; unitPrice: number }) =>
    repo.setDoctorPrice(command.doctorId, command.itemId, command.unitPrice),
  );
}

export function useClearDoctorPrice() {
  const repo = useFinanceRepository();
  return usePriceMutation((command: { doctorId: string; itemId: string }) =>
    repo.clearDoctorPrice(command.doctorId, command.itemId),
  );
}

/**
 * The currency, the dollar rate and the letterhead. Items 12 and 13 of the
 * same request. Read rarely: a rate changes when Accounts change it.
 */
export function useFinanceSettings(enabled = true) {
  const repo = useFinanceRepository();
  return useQuery({
    queryKey: keys.orders.financeSettings(),
    queryFn: () => repo.settings(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSetExchangeRate() {
  const repo = useFinanceRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (usdRate: number) => repo.setExchangeRate(usdRate),
    onSuccess: (settings) => {
      queryClient.setQueryData(keys.orders.financeSettings(), settings);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** What the lab is owed, by doctor and by how long. Asked only of somebody who takes payments. */
export function useFinanceOverview(enabled = true) {
  const repo = useFinanceRepository();
  return useQuery({
    queryKey: keys.orders.financeOverview(),
    queryFn: () => repo.overview(),
    enabled,
  });
}
